import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getFallbackSeo, normalizeSeoPath } from '../../data/seoDefaults';
import { getPublicSeo } from '../../services/seoPublicService';

function ensureMeta(selector, createAttributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }

  return element;
}

function setMetaByName(name, content) {
  const element = ensureMeta(`meta[name="${name}"]`, { name });
  element.setAttribute('content', content || '');
}

function setMetaByProperty(property, content) {
  const element = ensureMeta(`meta[property="${property}"]`, { property });
  element.setAttribute('content', content || '');
}

function removeMetaByName(name) {
  document.head.querySelector(`meta[name="${name}"]`)?.remove();
}

function removeMetaByProperty(property) {
  document.head.querySelector(`meta[property="${property}"]`)?.remove();
}

function setCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
}

function applySeo(seo) {
  const page = seo.page || {};
  const global = seo.global || {};
  const title = page.metaTitle || global.siteName || 'FactorStreet';
  const description = page.metaDescription || global.defaultMetaDescription || '';
  const keywords = page.metaKeywords || global.defaultMetaKeywords || '';
  const robots = page.robots || global.globalRobotsDefault || 'index, follow';
  const canonicalUrl = page.canonicalUrl || page.openGraph?.url || 'https://factorstreet.com/';
  const ogTitle = page.openGraph?.title || title;
  const ogDescription = page.openGraph?.description || description;
  const ogImage = page.openGraph?.image || '';
  const twitterTitle = page.twitter?.title || ogTitle;
  const twitterDescription = page.twitter?.description || ogDescription;
  const twitterImage = page.twitter?.image || ogImage;

  document.title = title;
  setMetaByName('description', description);
  setMetaByName('keywords', keywords);
  setMetaByName('robots', robots);
  setCanonical(canonicalUrl);

  setMetaByProperty('og:site_name', global.siteName || 'FactorStreet');
  setMetaByProperty('og:type', page.openGraph?.type || 'website');
  setMetaByProperty('og:title', ogTitle);
  setMetaByProperty('og:description', ogDescription);
  setMetaByProperty('og:url', canonicalUrl);

  if (ogImage) {
    setMetaByProperty('og:image', ogImage);
  } else {
    removeMetaByProperty('og:image');
  }

  setMetaByName('twitter:card', page.twitter?.card || global.defaultTwitterCardType || 'summary_large_image');
  setMetaByName('twitter:title', twitterTitle);
  setMetaByName('twitter:description', twitterDescription);

  if (twitterImage) {
    setMetaByName('twitter:image', twitterImage);
  } else {
    removeMetaByName('twitter:image');
  }
}

function SEOHead() {
  const location = useLocation();
  const pathname = useMemo(() => normalizeSeoPath(location.pathname), [location.pathname]);
  const [seo, setSeo] = useState(() => getFallbackSeo(pathname));

  useEffect(() => {
    let isMounted = true;
    const fallback = getFallbackSeo(pathname);

    if (pathname.startsWith('/admin')) {
      setSeo(fallback);
      return undefined;
    }

    setSeo(fallback);
    getPublicSeo(pathname).then((data) => {
      if (isMounted) {
        setSeo(data);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [pathname]);

  useEffect(() => {
    // CSR only: this updates hydrated browser metadata, while index.html remains the static fallback.
    applySeo(seo);
  }, [seo]);

  return null;
}

export default SEOHead;
